/**
 * DRR-211: Care Coordination ML-Ready Weights
 *
 * Hooks for shift planning and staff assignment optimization.
 * Backend uses ML-ready weight vectors for task prioritization and staff matching.
 *
 * Key concepts:
 * - Staffing Floors: Regulatory minimums (ULP ratios from 144G)
 * - Task Priority Weights: ML-trainable weights for prioritization
 * - Shift Plan: Optimized task-to-staff assignments for a shift window
 * - Replan: Dynamic re-optimization when conditions change (callout, emergency, etc.)
 */

import { useMemo } from 'react';
import { gql } from '@apollo/client/core';
import { useQuery } from '@apollo/client/react';
import {
  useGetCoordinationWeightsQuery,
  useGetActiveShiftPlanQuery,
  usePlanShiftMutation,
  useReplanShiftMutation,
  useZonesQuery,
  ReplanTriggerType,
  ZoneType,
} from '@/generated/graphql';
import { useAuth } from '@/auth';

// DRR-222: Flow Map types (temporary until codegen runs against live gateway)
// These match the backend GraphQL schema in coordination_api.rs
export enum FlowMapSourceType {
  Regulation = 'REGULATION',
  CarePlan = 'CARE_PLAN',
  TrainingReq = 'TRAINING_REQ',
  Compliance = 'COMPLIANCE',
}

export interface FlowMapSource {
  id: string;
  sourceType: FlowMapSourceType;
  label: string;
  taskCount: number;
}

export interface TaskCountBySource {
  sourceId: string;
  count: number;
}

export interface TaskCountBySubject {
  subjectId: string;
  count: number;
}

export interface FlowMapPerformer {
  actorId: string;
  displayName: string;
  role: string;
  taskCountBySource: TaskCountBySource[];
  taskCountBySubject: TaskCountBySubject[];
}

export interface TaskCountByPerformer {
  performerId: string;
  count: number;
}

export interface FlowMapSubject {
  actorId: string;
  displayName: string;
  taskCountByPerformer: TaskCountByPerformer[];
}

export interface FlowMapActivityBucket {
  actorId: string;
  hourOfDay: number;
  taskCount: number;
  intersectionCount: number;
}

export interface FlowMapIntersection {
  instanceId: string;
  actorIds: string[];
  timestamp: string;
}

export interface TenantFlowMap {
  sources: FlowMapSource[];
  performers: FlowMapPerformer[];
  subjects: FlowMapSubject[];
  activityBuckets: FlowMapActivityBucket[];
  intersections: FlowMapIntersection[];
}

/**
 * Get current coordination weights configuration.
 * Includes regulatory floors (from 144G) and learnable task priority weights.
 */
export function useCoordinationWeights() {
  const { data, loading, error } = useGetCoordinationWeightsQuery();

  return {
    weights: data?.coordinationWeights ?? null,
    loading,
    error,
  };
}

/**
 * Floor option for the floor selector.
 */
export interface FloorOption {
  value: string;
  label: string;
  floor: number;
  zoneCount: number;
  isMemoryCare: boolean;
}

/**
 * Get facility floors derived from zone data.
 * Returns unique floors with zone counts and memory care designation.
 *
 * DRR-108: Zone architecture (source of floor data).
 * DRR-211: Shift planning (consumer of floor data).
 */
export function useFacilityFloors() {
  const { tenant } = useAuth();
  const tenantNum = tenant?.num;
  const { data, loading, error } = useZonesQuery({
    variables: { tenantNum: tenantNum ?? 0 },
    skip: !tenantNum,
  });

  const floors = useMemo<FloorOption[]>(() => {
    if (!data?.zones) return [];

    // Group zones by floor number
    const floorMap = new Map<number, { zones: typeof data.zones; hasMemoryCare: boolean }>();

    for (const zone of data.zones) {
      const floorNum = zone.floor ?? 0;
      const existing = floorMap.get(floorNum) ?? { zones: [], hasMemoryCare: false };

      existing.zones.push(zone);
      // Check if any zone in this floor is residential with "memory" in name or description
      if (
        zone.zoneType === ZoneType.RESIDENTIAL &&
        (zone.name?.toLowerCase().includes('memory') ||
          zone.description?.toLowerCase().includes('memory'))
      ) {
        existing.hasMemoryCare = true;
      }

      floorMap.set(floorNum, existing);
    }

    // Convert to sorted array of FloorOptions
    const result: FloorOption[] = [];
    const sortedFloors = Array.from(floorMap.keys()).sort((a, b) => a - b);

    for (const floorNum of sortedFloors) {
      const info = floorMap.get(floorNum)!;
      // Skip floor 0 (outdoor/ground level) for shift planning
      if (floorNum === 0) continue;

      const label = info.hasMemoryCare
        ? `Floor ${floorNum} — Memory Care`
        : `Floor ${floorNum} — Main`;

      result.push({
        value: `floor-${floorNum}`,
        label,
        floor: floorNum,
        zoneCount: info.zones.length,
        isMemoryCare: info.hasMemoryCare,
      });
    }

    return result;
  }, [data?.zones]);

  return {
    floors,
    loading,
    error,
    hasFloors: floors.length > 0,
  };
}

/**
 * Get the active shift plan for a specific floor.
 */
export function useActiveShiftPlan(tenantId: string, floorId: string) {
  const { data, loading, error, refetch } = useGetActiveShiftPlanQuery({
    variables: { tenantId, floorId },
    skip: !tenantId || !floorId,
  });

  return {
    plan: data?.activeShiftPlan ?? null,
    loading,
    error,
    refetch,
  };
}

/**
 * Mutations for shift planning and replanning.
 */
export function useShiftPlanning() {
  const [planShift, { loading: planning }] = usePlanShiftMutation();
  const [replanShift, { loading: replanning }] = useReplanShiftMutation();

  return {
    planShift: async (
      tenantId: string,
      floorId: string,
      shiftStart?: Date,
      durationHours?: number
    ) => {
      const result = await planShift({
        variables: {
          tenantId,
          floorId,
          shiftStart: shiftStart?.toISOString(),
          durationHours,
        },
      });
      return result.data?.planShift ?? null;
    },

    replanShift: async (
      tenantId: string,
      planId: string,
      triggerType: ReplanTriggerType,
      options?: {
        staffId?: string;
        subjectId?: string;
        reason?: string;
      }
    ) => {
      const result = await replanShift({
        variables: {
          tenantId,
          planId,
          triggerType,
          staffId: options?.staffId,
          subjectId: options?.subjectId,
          reason: options?.reason,
        },
      });
      return result.data?.replanShift ?? null;
    },

    loading: planning || replanning,
  };
}

/**
 * Replan trigger types for staff-friendly display.
 */
export const REPLAN_TRIGGER_LABELS: Record<ReplanTriggerType, string> = {
  [ReplanTriggerType.STAFF_CALLOUT]: 'Staff Called Out',
  [ReplanTriggerType.EMERGENCY_ADMISSION]: 'Emergency Admission',
  [ReplanTriggerType.RESIDENT_INCIDENT]: 'Incident Response',
  [ReplanTriggerType.RESOURCE_UNAVAILABLE]: 'Resource Unavailable',
  [ReplanTriggerType.MANUAL_REQUEST]: 'Manual Replan',
};

// Re-export enums for convenience
export { ReplanTriggerType } from '@/generated/graphql';

// ═══════════════════════════════════════════════════════════════════════════════
// DRR-222: Tenant Flow Map Hooks
// ═══════════════════════════════════════════════════════════════════════════════

// GraphQL query for flow map (until codegen runs against live gateway)
const GET_TENANT_FLOW_MAP = gql`
  query GetTenantFlowMap($tenantId: String!, $dateRange: FlowMapDateRangeInput!) {
    tenantFlowMap(tenantId: $tenantId, dateRange: $dateRange) {
      sources {
        id
        sourceType
        label
        taskCount
      }
      performers {
        actorId
        displayName
        role
        taskCountBySource {
          sourceId
          count
        }
        taskCountBySubject {
          subjectId
          count
        }
      }
      subjects {
        actorId
        displayName
        taskCountByPerformer {
          performerId
          count
        }
      }
      activityBuckets {
        actorId
        hourOfDay
        taskCount
        intersectionCount
      }
      intersections {
        instanceId
        actorIds
        timestamp
      }
    }
  }
`;

/**
 * Get tenant flow map data for visualization.
 * Returns aggregated work stream data for Sankey, Chord, and Temporal views.
 */
export function useTenantFlowMap(
  tenantId: string,
  dateRange: { start: Date; end: Date }
) {
  const { data, loading, error, refetch } = useQuery<{ tenantFlowMap: TenantFlowMap }>(
    GET_TENANT_FLOW_MAP,
    {
      variables: {
        tenantId,
        dateRange: {
          start: dateRange.start.toISOString(),
          end: dateRange.end.toISOString(),
        },
      },
      skip: !tenantId,
    }
  );

  return {
    flowMap: data?.tenantFlowMap ?? null,
    loading,
    error,
    refetch,
  };
}

/**
 * Color mapping for flow map source types.
 */
export const FLOW_MAP_SOURCE_COLORS: Record<FlowMapSourceType, string> = {
  [FlowMapSourceType.Regulation]: 'red',
  [FlowMapSourceType.CarePlan]: 'blue',
  [FlowMapSourceType.TrainingReq]: 'grape',
  [FlowMapSourceType.Compliance]: 'orange',
};

/**
 * Labels for flow map source types.
 */
export const FLOW_MAP_SOURCE_LABELS: Record<FlowMapSourceType, string> = {
  [FlowMapSourceType.Regulation]: 'Regulation',
  [FlowMapSourceType.CarePlan]: 'Care Plan',
  [FlowMapSourceType.TrainingReq]: 'Training',
  [FlowMapSourceType.Compliance]: 'Compliance',
};
